import { useEffect, useState } from 'react'
import type { MouseEvent as Mausereignis } from 'react'
import { t, anzahlwort } from './sprache'
import { WEGE, wegschritte } from './Anleitungen'
import type { Weg } from './Anleitungen'
import {
  abhaken,
  anhaengen,
  istErledigt,
  leseStand,
  schluessel,
  schreibeStand,
  schrittVon,
  sprungSenden,
  vomModellErkannt,
  zaehlerstand,
  SPEICHER,
} from './Begleitliste'
import type { Begleitstand, Sprungziel, Zaehlerstand } from './Begleitliste'
import { Zustandssymbol } from './Zustand'

/**
 * Der Begleiter — die Anleitung als Abhakliste im zweiten Fenster (7.1
 * Abschnitt 10, R545–R549).
 *
 * **Er erklärt nichts, was die Hilfe nicht erklärt — er geht mit.** Die
 * Schritte kommen aus denselben Anleitungen (R545), der Klick am Schritt
 * bewegt das Hauptfenster (R547), und was das Modell weiß, hakt es selbst ab
 * (R546). Die Liste lebt im Browser und stirbt mit dem Verwerfen oder der
 * Abmeldung (R548).
 *
 * **Warum kein Weiter-Knopf.** Die Hilfe blättert, der Begleiter nicht: Jeder
 * Schritt ist jederzeit anklickbar und abhakbar, auch außer der Reihe (R549).
 * Der erste offene Schritt steht aufgeklappt da, damit man beim Blick auf den
 * zweiten Bildschirm sofort sieht, was dran ist — und nicht, weil die
 * anderen gesperrt wären.
 */
/**
 * **Der Name des danebengelegten Fensters** (R550).
 *
 * Er leistet zweierlei. Er ist das **Kriterium**: Ein Fenster, das so heißt,
 * liegt bereits daneben und bietet *Daneben legen* nicht noch einmal an — und
 * zwar unabhängig davon, wer es geöffnet hat. Und er ist die **Bremse**: Ein
 * zweiter Klick holt dasselbe Fenster nach vorn, statt ein drittes aufzumachen.
 *
 * Er ersetzt damit `window.opener`, das bis zum 2026-09-15 die Frage
 * beantwortete. Das tat es nur, solange jedes zweite Fenster aus einem
 * `window.open` entstand; seit die Öffnen-Punkte Links sind, sagt `opener`
 * nichts mehr über die Bauform des Fensters — nur noch darüber, wie es entstand.
 */
const NEBENFENSTER = 'coko-begleiter'

export function Begleiter({
  aufSprung,
}: {
  aufSprung: (bereich: string, unterpunkt?: string) => void
}) {
  const [stand, setStand] = useState<Begleitstand | null>(leseStand)
  const [heute, setHeute] = useState<Zaehlerstand>({})

  /*
    Die Zähler werden geholt, wenn jemand hinsieht: beim Start, beim Fokus,
    beim Sichtbarwerden — und alle dreißig Sekunden, solange das Fenster auf
    dem zweiten Bildschirm liegt und niemand es anfasst. Das ist der Preis
    dafür, dass ein Häkchen von selbst erscheint, während man im anderen
    Fenster die Einheit speichert.
  */
  useEffect(() => {
    let lebt = true
    const hole = () => {
      void zaehlerstand().then((z) => {
        if (lebt) setHeute(z)
      })
    }
    const beiSicht = () => {
      if (document.visibilityState === 'visible') hole()
    }

    hole()
    window.addEventListener('focus', hole)
    document.addEventListener('visibilitychange', beiSicht)
    const takt = window.setInterval(hole, 30_000)

    return () => {
      lebt = false
      window.removeEventListener('focus', hole)
      document.removeEventListener('visibilitychange', beiSicht)
      window.clearInterval(takt)
    }
  }, [])

  // Beide Fenster lesen denselben Speicher (R548): Hakt man im einen ab, zieht
  // das andere nach — über das Ereignis, das der Browser dafür hat.
  useEffect(() => {
    const beiSpeicher = (ereignis: StorageEvent) => {
      if (ereignis.key === null || ereignis.key === SPEICHER) setStand(leseStand())
    }
    window.addEventListener('storage', beiSpeicher)
    return () => window.removeEventListener('storage', beiSpeicher)
  }, [])

  function schreibe(neu: Begleitstand | null) {
    schreibeStand(neu)
    setStand(neu)
  }

  async function anfangen(weg: Weg) {
    // Die Ausgangslage ist der Stand von jetzt — nicht der vom letzten Holen,
    // sonst gälte ein Zähler als gestiegen, der nur älter geworden ist.
    const lage = await zaehlerstand()
    setHeute(lage)
    schreibe(anhaengen(leseStand() ?? stand, weg, lage))
  }

  function springe(ziel: Sprungziel) {
    sprungSenden(ziel, () => aufSprung(ziel.bereich, ziel.unterpunkt))
  }

  /**
   * Ein schmales Fenster für den zweiten Bildschirm — dieselbe Adresse,
   * dieselbe Schale (R530), nur die Maße sind die einer Liste.
   */
  const danebenAdresse = new URL('#/hilfe/begleiter', window.location.href).href

  function danebenLegen(e: Mausereignis<HTMLAnchorElement>) {
    /*
      **Die eigene Navigation des Links wird immer abbestellt** (R550). Sonst
      landete die Liste im Fenster, aus dem man sie danebenlegen wollte — und
      der Bildschirm, den sie begleiten soll, wäre weg. Was der Browser aus dem
      `window.open` macht, entscheidet er; **dass nichts Falsches passiert,
      wenn er nichts hergibt, entscheiden wir.**
    */
    e.preventDefault()
    window.open(danebenAdresse, NEBENFENSTER, 'popup=yes,width=460,height=900')
  }

  const zeilen = (stand?.schritte ?? []).map((eintrag) => {
    const schritt = schrittVon(eintrag)
    return {
      key: schluessel(eintrag),
      eintrag,
      schritt,
      erkannt: stand !== null && schritt !== null && vomModellErkannt(stand, schritt, heute),
      erledigt: stand !== null && istErledigt(stand, eintrag, schritt, heute),
    }
  })
  const offen = zeilen.filter((z) => !z.erledigt)
  const dran = offen[0]?.key ?? null
  const imNebenfenster = window.name === NEBENFENSTER

  return (
    <section className="hilfe begleiter">
      <h1>{t('bereich.hilfe.begleiter')}</h1>

      {!stand && <Wegwahl aufWahl={anfangen} vorhanden={new Set()} />}

      {stand && (
        <ol className="begleitliste">
          {zeilen.map(({ key, eintrag, schritt, erkannt, erledigt }, i) => (
            <li key={key} className={erledigt ? 'erledigt' : undefined}>
              <input
                type="checkbox"
                checked={erledigt}
                disabled={erkannt}
                aria-label={schritt?.titel ?? eintrag.schritt}
                title={erkannt ? t('begleiter.erkannt') : undefined}
                onChange={(e) => schreibe(abhaken(stand, eintrag, e.target.checked))}
              />
              {/* `open` folgt dem ersten offenen Schritt: Wird einer abgehakt,
                  klappt der nächste auf. Dazwischen darf der Anwender jeden
                  anderen aufklappen — React setzt das Attribut nur, wenn sich
                  sein Wert ändert. */}
              <details open={key === dran}>
                <summary aria-current={key === dran ? 'step' : undefined}>
                  <span className="nummer">{i + 1}</span>
                  <span className="name">{schritt?.titel ?? eintrag.schritt}</span>
                  {erkannt && <Zustandssymbol art="fertig" />}
                </summary>
                {schritt ? (
                  <>
                    <p>{schritt.text}</p>
                    {erkannt && <p className="gedaempft">{t('begleiter.erkannt')}</p>}
                    {schritt.ziel && (
                      <p className="handreichung">
                        <button type="button" className="ortsverweis" onClick={() => springe(schritt.ziel!)}>
                          {t('hilfe.dorthin')}
                        </button>
                      </p>
                    )}
                  </>
                ) : (
                  <p className="hinweis">{t('begleiter.unbekannt')}</p>
                )}
              </details>
            </li>
          ))}
        </ol>
      )}

      <p className="begleitfuss">
        {stand && (
          <span className="stand">
            {offen.length === 0
              ? t('begleiter.fertig')
              : t('begleiter.stand', { erledigt: anzahlwort(zeilen.length - offen.length), gesamt: anzahlwort(zeilen.length) })}
          </span>
        )}
        {!imNebenfenster && (
          /*
            Kein `rel="noopener"`: Bei gesetztem `noopener` verwirft der Browser
            den Fensternamen und öffnet doch ein zweites — dann gäbe es beim
            dritten Klick ein drittes Fenster. Das Ziel ist die eigene
            Anwendung, dieselbe Herkunft; es gibt hier nichts abzuschirmen.
          */
          <a href={danebenAdresse} target={NEBENFENSTER} onClick={danebenLegen}>
            {t('begleiter.daneben')}
          </a>
        )}
        {stand && (
          <button type="button" onClick={() => schreibe(null)}>
            {t('begleiter.verwerfen')}
          </button>
        )}
      </p>

      {stand && (
        <details className="begleitmehr">
          <summary>{t('begleiter.anhaengen')}</summary>
          <Wegwahl aufWahl={anfangen} vorhanden={new Set(zeilen.map((z) => z.key))} />
        </details>
      )}
    </section>
  )
}

/**
 * Die Wege zur Auswahl — ein Klick, und die Liste steht (R545).
 *
 * **Ein Weg, der schon ganz in der Liste steht, ist abgeschaltet.** Ein Knopf,
 * der nichts bewirkt, wird nicht angeboten (R280) — und das Anhängen bewirkte
 * nichts, weil jeder Schritt höchstens einmal steht. Der Anwender sieht am
 * Knopf, warum: Der Weg ist schon da.
 */
function Wegwahl({ aufWahl, vorhanden }: { aufWahl: (weg: Weg) => void; vorhanden: Set<string> }) {
  return (
    <ul className="wegwahl">
      {WEGE.map((weg) => {
        const schritte = wegschritte(weg)
        const schonDa = schritte.every(({ anleitung, schritt }) => vorhanden.has(`${anleitung}/${schritt.id}`))
        return (
          <li key={weg.id}>
            <button
              type="button"
              disabled={schonDa}
              title={schonDa ? t('begleiter.schonda') : undefined}
              onClick={() => aufWahl(weg)}
            >
              <span className="name">{t(weg.titel)}</span>
              {/* Ohne „5 Schritte" (7.2 Abschnitt 36, R1143). */}
              {schonDa && <span className="gedaempft">{t('begleiter.schonda')}</span>}
            </button>
          </li>
        )
      })}
    </ul>
  )
}
